import React, { RefObject, useEffect, useRef, useState } from 'react'

import { Icon, IconColor, IconName } from 'Components/Icon'

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

type BackToTopButtonProps = {
  scrollContainerRef?: RefObject<HTMLElement | null>
}

export const BackToTopButton: React.FC<BackToTopButtonProps> = ({ scrollContainerRef }) => {
  const [isVisible, setIsVisible] = useState(false)

  const buttonRef = useRef<HTMLButtonElement>(null)
  const contained = Boolean(scrollContainerRef)

  useEffect(() => {
    const container = scrollContainerRef?.current

    const updateVisibility = (): void => {
      const scrollTop = container ? container.scrollTop : window.scrollY

      const threshold = container ? container.clientHeight : window.innerHeight

      setIsVisible(scrollTop > threshold)
    }

    updateVisibility()

    const scrollTarget: HTMLElement | Window = container ?? window

    scrollTarget.addEventListener('scroll', updateVisibility, { passive: true })

    return () => scrollTarget.removeEventListener('scroll', updateVisibility)
  }, [scrollContainerRef])

  useEffect(() => {
    if (!isVisible) {
      buttonRef.current?.blur()
    }
  }, [isVisible])

  const scrollToTop = (): void => {
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth'

    const container = scrollContainerRef?.current

    if (container) {
      container.scrollTo({ top: 0, behavior })

      return
    }

    window.scrollTo({
      top: 0,
      behavior
    })
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      className={`backtotop${contained ? ' backtotop--contained' : ''}${isVisible ? ' is-visible' : ''}`}
      aria-label="Nach oben"
      tabIndex={isVisible ? undefined : -1}
      onMouseDown={(event) => event.preventDefault()}
      onClick={scrollToTop}
    >
      <Icon name={IconName.arrowup} color={IconColor.WHITE} ariaHidden />
    </button>
  )
}
